import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { AlertTriangle, ArrowDownLeft, ArrowUpRight, ChevronLeft, History, Wallet } from "lucide-react";
import api, { friendlyMessage } from "../../lib/api";
import EmptyState, { LoadingState } from "../../components/EmptyState";
import Pagination from "../../components/Pagination";
import {
  TRANSACTION_TYPES, amountTone, balanceText, deliveryDay, signedAmount, transactionLabel, transactionWhen, transactionsUrl,
} from "../../lib/customerTransactions";

const PAGE_SIZE = 20;
const NO_FILTERS = { type: "", start: "", end: "" };

/**
 * One customer's transaction history for the store admin — every wallet
 * top-up, delivery charge, refund, admin adjustment and bonus, newest first.
 * Opened from the Customers list ("History"). Read-only.
 *
 * A stacked list rather than a wide table, so it reads the same on a phone:
 * what happened and when on the left, the amount and the balance it left on
 * the right.
 */
export default function CustomerTransactions() {
  const { id } = useParams();
  const [filters, setFilters] = useState(NO_FILTERS);
  const [page, setPage] = useState(1);
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const load = () => {
    setBusy(true);
    setError(null);
    let stale = false;
    api.get(transactionsUrl(id, { ...filters, page, pageSize: PAGE_SIZE }))
      .then(({ data: next }) => { if (!stale) setData(next); })
      .catch((e) => { if (!stale) setError(friendlyMessage(e)); })
      .finally(() => { if (!stale) setBusy(false); });
    return () => { stale = true; };
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(load, [id, page, filters.type, filters.start, filters.end]);

  const setFilter = (patch) => { setPage(1); setFilters((f) => ({ ...f, ...patch })); };
  const filtered = !!(filters.type || filters.start || filters.end);

  if (error && !data) {
    return (
      <EmptyState icon={AlertTriangle} title="Couldn't load the transaction history" description={error}
        action={<button onClick={load} className="h-10 px-4 rounded-2xl bg-brand-sky text-white text-sm font-bold">Retry</button>}
        fullHeight />
    );
  }
  if (!data) return <LoadingState fullHeight />;

  const c = data.customer;
  const rows = data.transactions || [];
  return (
    <div className="space-y-5 page-enter" data-testid="customer-transactions-page">
      <div className="flex items-center gap-3">
        <Link to="/admin/customers" aria-label="Back to customers"
              className="w-10 h-10 rounded-full bg-white border border-neutral-200 flex items-center justify-center shrink-0" data-testid="back-btn">
          <ChevronLeft className="w-5 h-5" />
        </Link>
        <div className="min-w-0">
          <div className="text-xs uppercase tracking-wider font-bold text-neutral-500">Transaction history</div>
          <h1 className="font-heading text-2xl sm:text-3xl font-extrabold tracking-tight break-words" data-testid="txn-customer-name">{c.name || "—"}</h1>
          <div className="text-sm text-neutral-600 font-mono" data-testid="txn-customer-phone">{c.phone}</div>
        </div>
      </div>

      {/* Compact on a phone so the list starts on the first screen: the
          balance on its own row, money in / out side by side — wide enough
          that a six- or seven-figure total stays on one line. */}
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 sm:gap-3">
        <div className="col-span-2 sm:col-span-1 bg-white rounded-2xl sm:rounded-3xl border border-neutral-200 p-3 sm:p-4 min-w-0" data-testid="txn-wallet-balance">
          <div className="text-[10px] uppercase tracking-wider font-bold text-neutral-500 flex items-center gap-1"><Wallet className="w-3.5 h-3.5 shrink-0 hidden sm:block" /> Wallet now</div>
          <div className={`font-heading text-lg sm:text-2xl font-extrabold mt-1 break-words ${data.wallet_balance < 0 ? "text-brand-clay" : ""}`}>{balanceText(data.wallet_balance)}</div>
        </div>
        <div className="bg-white rounded-2xl sm:rounded-3xl border border-neutral-200 p-3 sm:p-4 min-w-0" data-testid="txn-money-in">
          <div className="text-[10px] uppercase tracking-wider font-bold text-neutral-500">Money in{filtered ? " (filtered)" : ""}</div>
          <div className="font-heading text-lg sm:text-2xl font-extrabold mt-1 break-words text-brand-leaf">{signedAmount(data.summary.credited)}</div>
        </div>
        <div className="bg-white rounded-2xl sm:rounded-3xl border border-neutral-200 p-3 sm:p-4 min-w-0" data-testid="txn-money-out">
          <div className="text-[10px] uppercase tracking-wider font-bold text-neutral-500">Money out{filtered ? " (filtered)" : ""}</div>
          <div className="font-heading text-lg sm:text-2xl font-extrabold mt-1 break-words text-brand-clay">{signedAmount(-data.summary.debited)}</div>
        </div>
      </div>

      <section className="bg-white rounded-3xl border border-neutral-200 overflow-hidden">
        <div className="p-4 border-b border-neutral-100 grid grid-cols-2 sm:grid-cols-4 gap-3 items-end">
          <div className="col-span-2 sm:col-span-1">
            <label className="text-[10px] uppercase tracking-wider font-bold text-neutral-500" htmlFor="txn-type">Type</label>
            <select id="txn-type" value={filters.type} onChange={(e) => setFilter({ type: e.target.value })}
                    className="h-11 px-3 rounded-2xl border border-neutral-200 bg-white w-full mt-1" data-testid="txn-filter-type">
              <option value="">All transactions</option>
              {TRANSACTION_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
            </select>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider font-bold text-neutral-500" htmlFor="txn-from">From</label>
            <input id="txn-from" type="date" value={filters.start} max={filters.end || undefined}
                   onChange={(e) => setFilter({ start: e.target.value })}
                   className="h-11 px-3 rounded-2xl border border-neutral-200 bg-white w-full mt-1" data-testid="txn-filter-from" />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider font-bold text-neutral-500" htmlFor="txn-to">To</label>
            <input id="txn-to" type="date" value={filters.end} min={filters.start || undefined}
                   onChange={(e) => setFilter({ end: e.target.value })}
                   className="h-11 px-3 rounded-2xl border border-neutral-200 bg-white w-full mt-1" data-testid="txn-filter-to" />
          </div>
          <div className="col-span-2 sm:col-span-1 flex items-center justify-between gap-3">
            <div className="text-sm text-neutral-600" data-testid="txn-count">
              {data.total} transaction{data.total === 1 ? "" : "s"}
            </div>
            {filtered && (
              <button type="button" onClick={() => { setPage(1); setFilters(NO_FILTERS); }}
                      className="h-11 px-4 rounded-2xl border border-neutral-200 text-sm font-bold text-brand-sky" data-testid="txn-filter-clear">
                Clear
              </button>
            )}
          </div>
        </div>

        {error && <div className="px-4 py-3 text-sm text-brand-clay" data-testid="txn-error">{error}</div>}

        {/* Dimmed while a page or a filter is loading — also when the last
            result was empty, so a change of filter never looks ignored. */}
        <div className={busy ? "opacity-60" : ""} aria-busy={busy} data-testid="txn-results">
        {rows.length === 0 ? (
          <EmptyState icon={History} size="sm"
                      title={filtered ? "No transactions match these filters" : "No transactions yet"}
                      description={filtered ? "Try another type or a wider date range." : "Top-ups, delivery charges, refunds and adjustments for this customer will show up here."}
                      testId="txn-empty" />
        ) : (
          <ul className="divide-y divide-neutral-100" data-testid="txn-list">
            {rows.map((t) => {
              const tone = amountTone(t.amount);
              const Icon = tone === "in" ? ArrowDownLeft : ArrowUpRight;
              return (
                <li key={t.id} className="px-4 py-3 flex items-start gap-3" data-testid={`txn-${t.id}`}>
                  <div className={`w-9 h-9 rounded-2xl flex items-center justify-center shrink-0 ${tone === "in" ? "bg-brand-leaf/10 text-brand-leaf" : tone === "out" ? "bg-brand-clay/10 text-brand-clay" : "bg-neutral-100 text-neutral-500"}`}>
                    <Icon className="w-4 h-4" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="font-semibold break-words" data-testid={`txn-desc-${t.id}`}>{t.description || transactionLabel(t.type)}</div>
                    <div className="text-xs text-neutral-500 mt-0.5">
                      <span className="font-bold text-neutral-600" data-testid={`txn-type-${t.id}`}>{transactionLabel(t.type)}</span>
                      {" · "}<span data-testid={`txn-when-${t.id}`}>{transactionWhen(t.created_at)}</span>
                    </div>
                    {(t.delivery_date || t.reference) && (
                      <div className="text-xs text-neutral-500 break-all">
                        {t.delivery_date && <span data-testid={`txn-delivery-${t.id}`}>For delivery on {deliveryDay(t.delivery_date)}{t.delivery_slot ? ` (${t.delivery_slot})` : ""}</span>}
                        {t.delivery_date && t.reference ? " · " : ""}
                        {t.reference && <span data-testid={`txn-ref-${t.id}`}>Ref {t.reference}</span>}
                      </div>
                    )}
                  </div>
                  <div className="text-right shrink-0">
                    <div className={`font-mono font-bold ${tone === "in" ? "text-brand-leaf" : tone === "out" ? "text-brand-clay" : "text-neutral-500"}`} data-testid={`txn-amount-${t.id}`}>
                      {signedAmount(t.amount)}
                    </div>
                    <div className="text-xs text-neutral-500 font-mono" data-testid={`txn-balance-${t.id}`}>Bal {balanceText(t.balance_after)}</div>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
        </div>

        <div className="px-4 pb-4">
          <Pagination page={data.page} totalPages={data.total_pages} total={data.total} pageSize={data.page_size}
                      onPageChange={setPage} loading={busy} testId="txn-pagination" />
        </div>
      </section>
    </div>
  );
}
